import { differenceInMinutes } from 'date-fns';
import sortBy from 'lodash/sortBy';
import { v4 as uuidv4 } from 'uuid';
import gettext from '@sdk/lang/gettext';
import { TimesheetEntry, TimesheetEntryType } from '@sdk/timesheets/types/TimesheetEntry';
import { amendOrderOfEntries, getBaseSequenceId } from '@sdk/timesheets/helpers/utils';
import { AllBreakTypes, BreakCategory, AutomatedBreakType } from '@sdk/settings/types';
import { getEstimatedBreaksDuration } from '@sdk/timesheets/helpers/getEstimatedBreaksDuration';
import { sumUnpaidBreaksMinutes } from '@sdk/timeClock/utils/sumUnpaidBreaksMinutes';
import { extendBreaksWithExcessiveFlag } from '@sdk/timeClock/utils/extendBreaksWithExcessiveFlag';
import {
  CountOfBreaksTaken,
  BreakEntryInfo,
  AutomatedBreakEntryPaidInfo,
} from '@sdk/timeClock/types';
import { isBreakPaid } from '@sdk/timeClock/utils/isBreakPaid';
import { BreakAttestation } from '@sdk/automatedBreaks/types/Attestation';

import { getBreakEntriesWithDates } from '@web/timesheets/CreateTimesheetModal/utils/breakUtils';
import { getClockInClockOutDates } from '@web/timesheets/CreateTimesheetModal/createTimesheetModal.utils';
import {
  TimeClockEntry,
  TimeClockEntryWithDates,
  GetTotalDurationProps,
  MarkInvalidTimeClockEntriesProps,
  MaybeClockIn,
} from '@web/timesheets/CreateTimesheetModal/createTimesheetModal.types';

/**
 * Small helper to quickly create new time clock entry on create timesheet modal
 */
export const getEmptyTimeClockEntry = (): TimeClockEntry => ({
  key: uuidv4(),
  clockIn: '',
  clockOut: '',
  baseSequenceId: null,
  breaks: [],
  deleted: false,
});

/**
 * Used to calculate duration in minutes based on clock in and clock outs **only**.
 * It subtracts the provided unpaidBreakMinutes
 * On top of that funtion does not return negative values (lowest it can return 0).
 */
export const getTotalDurationInMinutes = ({
  timeClockEntriesWithDates,
  unpaidBreakMinutes,
}: GetTotalDurationProps) => {
  const durationInMinutes = timeClockEntriesWithDates.reduce(
    (acc, { clockIn, clockOut, deleted }) => {
      const value = !clockIn || !clockOut || deleted ? 0 : differenceInMinutes(clockOut, clockIn);
      return acc + value;
    },
    0
  );

  return Math.max(0, durationInMinutes - unpaidBreakMinutes);
};

/**
 * Used to calculate total unpaid break minutes based on live form data
 * in timeClockEntries. If a break is supposed to be paid but it's duration
 * exceeds the expected duration then the overage is counted as unpaid:
 *   - per break for automated breaks
 *   - cumulatively for standard breaks
 */

export const calculateUnpaidBreakMinutes = ({
  shift,
  currentUser,
  entriesDate,
  timeClockEntries,
  automatedBreaks,
  automatedBreaksFeatureEnabled,
  breakAttestations = [],
}) => {
  const scheduledBreaksDuration = getEstimatedBreaksDuration({
    shift,
    automatedBreaksSettings: automatedBreaks,
    orgSettings: currentUser.orgSettings,
  });

  const unpaidBreakMinutes = timeClockEntries.reduce((tally, block) => {
    const { clockIn, clockOut } = getClockInClockOutDates(
      entriesDate,
      block.clockIn,
      block.clockOut
    );

    const breaksAccountedFor: CountOfBreaksTaken = {
      [AllBreakTypes.standard]: 0,
      [AllBreakTypes.meal]: 0,
      [AllBreakTypes.rest]: 0,
    };

    const existingProjection = shift?.timesheetProjections?.find(
      (projection) => getBaseSequenceId(projection.id) === block.baseSequenceId
    );
    (existingProjection?.breakAttestations || []).forEach((attestation: BreakAttestation) => {
      breaksAccountedFor[attestation.breakType]++;
    });

    const extendedBreaks = extendBreaksWithExcessiveFlag({
      breaks: mapBreaksFromForm({
        breaks: block.breaks,
        clockIn,
        entriesDate,
        automatedBreaksFeatureEnabled,
        automatedBreaks,
        currentUser,
      }),
      clockIn,
      clockOut,
      automatedBreaksSettings: automatedBreaks,
      breaksTaken: breaksAccountedFor,
    });

    return (
      tally +
      sumUnpaidBreaksMinutes({
        breaks: extendedBreaks,
        clockOut,
        scheduledBreaksDuration,
        capturedAutomatedBreaksSettings: {
          automatedBreakSettingMealDuration:
            block.clockInEntry?.automatedBreakSettingMealDuration ||
            currentUser.orgSettings.automatedBreaksMealBreakDuration,
          automatedBreakSettingRestDuration:
            block.clockInEntry?.automatedBreakSettingRestDuration ||
            currentUser.orgSettings.automatedBreaksRestBreakDuration,
          automatedBreakSettingMealIsPaid:
            block.clockInEntry?.automatedBreakSettingMealIsPaid ||
            currentUser.orgSettings.automatedBreaksMealIsPaid,
          automatedBreakSettingRestIsPaid:
            block.clockInEntry?.automatedBreakSettingRestIsPaid ||
            currentUser.orgSettings.automatedBreaksRestIsPaid,
        },
        breakAttestations: breakAttestations.filter(
          (a) => a.baseSequenceId === block.baseSequenceId
        ),
      })
    );
  }, 0);

  return unpaidBreakMinutes;
};

/**
 * Handle date parsing after hours of clockIn and clockOut are selected.
 * This means if you choose clockOut time that is earlier than clockIn
 * you could use this function to make clockOut the next day.
 *
 * @param timeClockEntries TimeClockEntry
 * @param date Date
 * @returns TimeClockEntryWithDates[]
 */
export const getTimeClockEntriesWithDates = (
  timeClockEntries: TimeClockEntry[],
  date: Date
): TimeClockEntryWithDates[] =>
  timeClockEntries.map(({ clockIn, clockOut, breaks, ...rest }) => {
    const clockInClockOutDates = getClockInClockOutDates(date, clockIn, clockOut);

    return {
      ...rest,
      ...clockInClockOutDates,
      breaks: getBreakEntriesWithDates(breaks, clockInClockOutDates.clockIn || date),
    };
  });

/**
 * Return timeClock entries sorted by clock in times, from earliest to latest.
 *
 * @param timeClockEntries TimeClockEntryWithDates[]
 * @returns
 */
export const getSortedTimeClockEntries = <T extends MaybeClockIn>(timeClockEntries: T[]): T[] => {
  const sortedEntries = [...timeClockEntries];
  const nowTimestamp = new Date().getTime();
  sortedEntries.sort((te1, te2) => {
    const timestamp1 = te1.clockIn?.getTime() || nowTimestamp;
    const timestamp2 = te2.clockIn?.getTime() || nowTimestamp;
    return timestamp1 - timestamp2;
  });
  return sortedEntries;
};

/**
 * Check if clock in and clock out times of various sections of time clock are overlapping with each other.
 * It returns object with keys being ids of entries that are not valid.
 * @param timeClockEntries TimeClockEntryWithDates[]
 * @returns
 */
export const getOverlappingTimeClockEntries = (timeClockEntries: TimeClockEntryWithDates[]) => {
  const sortedEntries = getSortedTimeClockEntries(timeClockEntries).filter(
    ({ deleted }) => !deleted
  );

  let previousEntry: TimeClockEntryWithDates;
  const result = sortedEntries.reduce((acc, timeclockEntry) => {
    if (!previousEntry) {
      // First entry so we can't yet decide if it's overlapping
      previousEntry = timeclockEntry;
      return acc;
    }

    if (
      previousEntry.clockOut &&
      timeclockEntry.clockIn &&
      previousEntry.clockOut.getTime() > timeclockEntry.clockIn.getTime()
    ) {
      acc[previousEntry.key] = acc[timeclockEntry.key] = gettext(
        'Timesheets cannot overlap on each other'
      );
    }

    previousEntry = timeclockEntry;
    return acc;
  }, {});

  return result;
};

/**
 * Loop through set off errors, if there are timeClock errors then mark both fields (clockIn, clockOut)
 * for each invalid entry, using shopify/react-form field API.
 */
export const markInvalidTimeClockEntries = ({
  formErrors,
  timeClockFields,
}: MarkInvalidTimeClockEntriesProps) => {
  if (formErrors.timeClock && Object.keys(formErrors.timeClock).length) {
    Object.keys(formErrors.timeClock).forEach((entryId) => {
      const errorMessage = formErrors.timeClock[entryId];
      const { clockIn, clockOut } = timeClockFields.find(({ key }) => key.value === entryId);
      clockIn.setError(errorMessage);
      clockOut.setError(errorMessage);
    });
  }
};

/**
 * Creates timeclock entries for form to be populated out of timesheet entries coming from API.
 * @param timesheets
 * @returns
 */
export const breakDownTimesheetsToGroups = (timesheets: TimesheetEntry[]) => {
  const orderedByBaseSeq = sortBy(timesheets, 'baseSequenceId');
  let lastBaseSeqId: number;
  const groups = orderedByBaseSeq.reduce((timesheetGroups, timesheet) => {
    if (!lastBaseSeqId || lastBaseSeqId !== timesheet.baseSequenceId) {
      timesheetGroups.push([]);
    }

    lastBaseSeqId = timesheet.baseSequenceId;

    timesheetGroups[timesheetGroups.length - 1].push(timesheet);
    return timesheetGroups;
  }, []);

  return groups.map((group) => amendOrderOfEntries(sortBy(group, 'timestamp', 'sequenceId')));
};

/**
 * List of timesheet entries types that are not visible in edit timesheet form.
 */
export const ListOfTimesheetEntriesToIgnore = [
  TimesheetEntryType.deleteParent,
  TimesheetEntryType.markApproved,
  TimesheetEntryType.markPending,
  TimesheetEntryType.markConsentPending,
  TimesheetEntryType.markConsentGiven,
  TimesheetEntryType.markConsentDenied,
];

/**
 * Filter out timesheet entries that are not supposed to be presented in edit timesheet form.
 * @param timesheetEntries TimesheetEntry[]
 * @returns
 */
export const filterTimesheetEntriesForForm = <T extends { type: TimesheetEntryType }>(
  timesheetEntries: T[]
): T[] => timesheetEntries.filter(({ type }) => !ListOfTimesheetEntriesToIgnore.includes(type));

export const mapBreaksFromForm = ({
  breaks,
  clockIn,
  entriesDate,
  automatedBreaksFeatureEnabled,
  automatedBreaks,
  currentUser,
}) => {
  return breaks.map((breakEntry): BreakEntryInfo => {
    const { clockIn: breakStart, clockOut: breakEnd } = getClockInClockOutDates(
      clockIn || entriesDate,
      breakEntry.start,
      breakEntry.end,
      { isDateWithTime: true }
    );

    const automatedBreakEntryPaidInfo = breakEntry.startEntry
      ? (breakEntry.startEntry as unknown as AutomatedBreakEntryPaidInfo)
      : {
          automatedBreakType: breakEntry.type as unknown as AutomatedBreakType,
          automatedBreakSettingMealIsPaid: null,
          automatedBreakSettingRestIsPaid: null,
        };

    return {
      breakStart,
      breakEnd,
      type: breakEntry.type,
      isPaid: isBreakPaid({
        breakEntry: automatedBreakEntryPaidInfo,
        automatedBreaksFeatureEnabled,
        automatedBreaksSettings: automatedBreaks,
        areStandardBreaksPaid: currentUser.orgSettings.shiftBreakType === BreakCategory.paid,
      }),
    };
  });
};
